import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { UNAUTHORIZED_STATUS_CODE } from '@constants/api.ts';
import { configureStore, createListenerMiddleware, isFulfilled, isRejected } from '@reduxjs/toolkit';
import { doLogin, updateUserConsentInfo } from '@routes/auth/login/model/login.slice.ts';
import { startAdaptiveQuiz } from '@routes/sidebar/learning/___pages___/learning-overview/model/adaptiveQuiz.slice.ts';
import {
    finishFinalQuizAndUpdatePathwayConditionally,
    startFinalQuiz,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/finalQuiz.slice.ts';
import {
    sentCompletedPathwayElement,
    sentVisitedPathwayElement,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/learningOverview.slice.ts';
import {
    finishPracticeQuiz,
    startPracticeQuiz,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/practiceQuiz.slice.ts';
import { getPoints } from '@routes/sidebar/learning/model/learning.slice.ts';
import { setAlert } from '@shared/model/alert.slice.ts';
import { renderAlertErrorDesc } from '@utils/general.tsx';

import rootReducer from './rootReducer.ts';

/*
EXAMPLE
1) A default alert has already been shown by our listener middleware.
2) If you need a truly custom banner/message, you can call:
    .then(() => {
        dispatch(
            setAlert({
                type: ALERT_STATUSES.SUCCESS,
                message: AlertNotifications[ALERT_STATUSES.SUCCESS].message,
                description: 'your desc',
            })
        );
    })
    .catch((error) => {
        dispatch(
            setAlert({
                type: ALERT_STATUSES.ERROR,
                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                description: 'your desc',
            })
        );
    });
});
3) Otherwise you can leave this block empty, or add any other logic
   (e.g. logging, rollback, analytics).
});
*/

// In-memory flag to globally enable/disable the listener
let globalAlertsEnabled = true;

export const disableGlobalAlerts = () => {
    globalAlertsEnabled = false;
};

export const enableGlobalAlerts = () => {
    globalAlertsEnabled = true;
};

type MatcherAction<M> = M extends (action: any) => action is infer A ? A : never;

const isRejectedAction = isRejected();

type RejectedAction = MatcherAction<typeof isRejectedAction>;

type StatusPayload = {
    status?: number | string;
    statusText?: string;
};

const hasStatus = (value: unknown): value is StatusPayload =>
    typeof value === 'object' && value !== null && 'status' in value;

const isUnauthorized = (actionPayload: unknown) =>
    hasStatus(actionPayload) && actionPayload.status === UNAUTHORIZED_STATUS_CODE;

// It will only fire when `globalAlertsEnabled` is true and user authorized.
const listener = createListenerMiddleware();
listener.startListening({
    matcher: isRejectedAction,
    effect: async (action, listenerApi) => {
        const rejectedAction = action as RejectedAction;

        if (!globalAlertsEnabled || isUnauthorized(rejectedAction.payload)) return;

        const errorSource = (rejectedAction.payload ?? rejectedAction.error) as StatusPayload;
        const { status = '', statusText = '' } = errorSource;

        const description =
            typeof rejectedAction.payload === 'string'
                ? rejectedAction.payload
                : renderAlertErrorDesc(`${status}`, statusText);

        listenerApi.dispatch(
            setAlert({
                type: ALERT_STATUSES.ERROR,
                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                description,
            }),
        );
    },
});

//we check the specific action types
const includeActionTypes = [
    updateUserConsentInfo.fulfilled.type,
    doLogin.fulfilled.type,
    sentCompletedPathwayElement.fulfilled.type,
    sentVisitedPathwayElement.fulfilled.type,
    startPracticeQuiz.fulfilled.type,
    startFinalQuiz.fulfilled.type,
    startAdaptiveQuiz.fulfilled.type,
    finishPracticeQuiz.fulfilled.type,
    finishFinalQuizAndUpdatePathwayConditionally.fulfilled.type,
];

// this listener will fire when the specific actions are dispatched to get points
listener.startListening({
    predicate: (action) => isFulfilled(action) && includeActionTypes.includes(action.type),
    effect: async (action, listenerApi) => {
        const state = listenerApi.getState() as RootState;
        //case when user has to update consents or login for the first time and need to accept consents
        if (action.type === updateUserConsentInfo.fulfilled.type) {
            const userConsentInfo = state.authLogin.userConsentInfo;

            if (userConsentInfo) {
                void listenerApi.dispatch(getPoints());
            }
        } else {
            void listenerApi.dispatch(getPoints());
        }
    },
});

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({ serializableCheck: false }).prepend(listener.middleware),
    // Turn DevTools on in dev mode (import.meta.env.MODE === 'development') and off in prod
    devTools: import.meta.env.MODE === 'development',
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
